import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Field } from "@/pages/Companies";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Wrench, Pencil, Trash2, User, FileText, PencilRuler, HardHat } from "lucide-react";
import { fmtDate, FENCE_LABELS } from "@/lib/constants";
import ClientSearchSelect from "@/components/ClientSearchSelect";

const STATUS_COLS = ["queued", "cutting", "welding", "galvanising", "ready", "delivered"];
const STATUS_LABELS = { queued: "Queued", cutting: "Cutting", welding: "Welding", galvanising: "Galvanising", ready: "Ready", delivered: "Delivered" };
const STATUS_BG = {
  queued: "border-slate-300 bg-slate-50",
  cutting: "border-amber-300 bg-amber-50",
  welding: "border-cyan-300 bg-cyan-50",
  galvanising: "border-violet-300 bg-violet-50",
  ready: "border-emerald-300 bg-emerald-50",
  delivered: "border-emerald-400 bg-emerald-100",
};

const EMPTY = { title: "", item_type: "custom_gate", assignee: "", installer: "", blueprint_id: "", scheduled_start: "", scheduled_end: "", status: "queued", qty: 1, notes: "", company_id: "", client_name: "", quote_id: "", quote_number: "" };

const openBlueprintPdf = async (bid) => {
  try {
    const res = await api.get(`/blueprints/${bid}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    toast.error("Could not open blueprint PDF");
  }
};

export default function Fabrication() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const { data: fabs = [] } = useQuery({ queryKey: ["fabs"], queryFn: async () => (await api.get("/fabrications")).data });
  const { data: fenceTypes = [] } = useQuery({ queryKey: ["fence-types"], queryFn: async () => (await api.get("/fence-types")).data });
  const { data: team = [] } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/auth/team")).data });
  const { data: clientDetail } = useQuery({
    queryKey: ["fab-client", editing?.company_id],
    queryFn: async () => (await api.get(`/clients/${editing.company_id}`)).data,
    enabled: !!(open && editing?.company_id),
  });
  const clientQuotes = clientDetail?.quotes || [];

  const save = useMutation({
    mutationFn: async (body) => {
      const payload = {
        ...body, qty: parseInt(body.qty) || 1,
        company_id: body.company_id || null, client_name: body.client_name || null,
        quote_id: body.quote_id || null, quote_number: body.quote_number || null,
      };
      if (body.id) return (await api.patch(`/fabrications/${body.id}`, payload)).data;
      return (await api.post("/fabrications", payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["fabs"] }); setOpen(false); toast.success("Saved"); },
  });
  const advance = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/fabrications/${id}`, { status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fabs"] }),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/fabrications/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fabs"] }),
  });

  return (
    <div>
      <PageHeader
        title="Fabrication Workshop"
        subtitle="Workshop queue for custom gates, panels, bollards & handrails — track from cutting to galvanising to delivery."
        breadcrumb={["Operations", "Fabrication"]}
        actions={
          <Button data-testid="new-fab-btn" onClick={() => { setEditing({ ...EMPTY }); setOpen(true); }} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Plus size={16} className="mr-2" /> New work item
          </Button>
        }
      />
      <div className="px-6 lg:px-10 py-6">
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="fab-board">
          {STATUS_COLS.map((s) => {
            const items = fabs.filter((f) => f.status === s);
            return (
              <div key={s} className={`flex-shrink-0 w-64 border ${STATUS_BG[s]} transition-colors`}>
                <div className="px-3 py-2.5 border-b border-slate-200 bg-white flex items-center justify-between">
                  <div className="stamp-label text-slate-700">{STATUS_LABELS[s]}</div>
                  <Badge variant="outline" className="rounded-sm text-[10px]">{items.length}</Badge>
                </div>
                <div className="p-2 space-y-2 min-h-[160px]">
                  {items.map((f) => {
                    const nextIdx = STATUS_COLS.indexOf(s) + 1;
                    const nextStatus = STATUS_COLS[nextIdx];
                    return (
                      <div key={f.id} className="bg-white border border-slate-200 p-2.5 hover:border-cyan-400 group" data-testid={`fab-card-${f.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-mono text-[10px] text-slate-500">{f.fab_number}</div>
                          <div className="font-mono text-xs font-bold text-cyan-700">×{f.qty}</div>
                        </div>
                        <div className="text-xs font-medium text-slate-900 mt-1 leading-tight">{f.title}</div>
                        {f.item_type && <div className="text-[10px] text-slate-500 mt-0.5">{FENCE_LABELS[f.item_type] || f.item_type}</div>}
                        {f.client_name && (
                          <div className="text-[10px] text-emerald-700 font-medium mt-1 flex items-center gap-1 cursor-pointer hover:underline"
                            onClick={() => f.company_id && (window.location.href = `/clients/${f.company_id}`)}
                            data-testid={`fab-client-link-${f.id}`}>
                            <User size={10} /> {f.client_name}
                          </div>
                        )}
                        {f.quote_number && (
                          <div className="text-[10px] text-cyan-700 font-mono mt-0.5 flex items-center gap-1">
                            <FileText size={10} /> {f.quote_number}
                          </div>
                        )}
                        {f.assignee && <div className="text-[10px] text-cyan-700 font-medium mt-1">👷 Fab: {f.assignee}</div>}
                        {f.installer && <div className="text-[10px] text-emerald-700 font-medium mt-0.5 flex items-center gap-1"><HardHat size={10} /> Install: {f.installer}</div>}
                        <div className="flex items-center gap-1 mt-1">
                          {f.blueprint_id && (
                            <button type="button" onClick={() => openBlueprintPdf(f.blueprint_id)}
                              className="text-[10px] text-violet-700 font-medium flex items-center gap-1 hover:underline"
                              data-testid={`fab-blueprint-pdf-${f.id}`}>
                              <PencilRuler size={10} /> Blueprint PDF
                            </button>
                          )}
                          {f.auto_generated && <Badge variant="outline" className="rounded-sm text-[8px] border-cyan-200 bg-cyan-50 text-cyan-700 px-1">AUTO</Badge>}
                        </div>
                        <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {nextStatus && <Button size="sm" data-testid={`advance-fab-${f.id}`} onClick={() => advance.mutate({ id: f.id, status: nextStatus })} className="h-6 text-[10px] rounded-sm bg-cyan-600 hover:bg-cyan-700 px-1.5">→ {STATUS_LABELS[nextStatus]}</Button>}
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { setEditing({ ...f }); setOpen(true); }}><Pencil size={11} /></Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-rose-600" onClick={() => del.mutate(f.id)}><Trash2 size={11} /></Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-xl">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing?.id ? "Edit work item" : "New fabrication item"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3">
                <Field label="Client (links to their timeline)">
                  <ClientSearchSelect
                    value={editing.company_id}
                    currentLabel={editing.client_name}
                    testid="fab-client-select"
                    onSelect={(c) => setEditing({ ...editing, company_id: c.id, client_name: c.name, quote_id: "", quote_number: "" })}
                  />
                </Field>
                {editing.company_id && (
                  <Field label="Linked quote">
                    <Select value={editing.quote_id || "none"} onValueChange={(v) => {
                      if (v === "none") setEditing({ ...editing, quote_id: "", quote_number: "" });
                      else {
                        const q = clientQuotes.find((x) => x.id === v);
                        setEditing({
                          ...editing, quote_id: v, quote_number: q?.quote_number || "",
                          title: editing.title || `${q?.quote_number || ""} — ${q?.fence_type ? (FENCE_LABELS[q.fence_type] || q.fence_type) : "fabrication"}`.trim(),
                        });
                      }
                    }}>
                      <SelectTrigger data-testid="fab-quote-select"><SelectValue placeholder="— No quote —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— No quote —</SelectItem>
                        {clientQuotes.map((q) => (
                          <SelectItem key={q.id} value={q.id}>{q.quote_number} · {q.status} · {q.site_address || q.customer_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
              </div>
              <Field label="Title" required><Input data-testid="fab-title-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Item type">
                  <Select value={editing.item_type} onValueChange={(v) => setEditing({ ...editing, item_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{fenceTypes.filter(f => !["labour","incidental"].includes(f.value)).map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Qty"><Input type="number" value={editing.qty} onChange={(e) => setEditing({ ...editing, qty: e.target.value })} /></Field>
                <Field label="Fabricator">
                  <Input list="fab-team-list" value={editing.assignee || ""} onChange={(e) => setEditing({ ...editing, assignee: e.target.value })} placeholder="Pick or type a name" data-testid="fab-assignee-input" />
                </Field>
                <Field label="Installer">
                  <Input list="fab-team-list" value={editing.installer || ""} onChange={(e) => setEditing({ ...editing, installer: e.target.value })} placeholder="Pick or type a name" data-testid="fab-installer-input" />
                </Field>
                <datalist id="fab-team-list">
                  {team.map((u) => <option key={u.id} value={u.name}>{u.role}</option>)}
                </datalist>
                <Field label="Status">
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_COLS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Scheduled start"><Input type="date" value={editing.scheduled_start ? editing.scheduled_start.slice(0,10) : ""} onChange={(e) => setEditing({ ...editing, scheduled_start: e.target.value })} /></Field>
                <Field label="Scheduled end"><Input type="date" value={editing.scheduled_end ? editing.scheduled_end.slice(0,10) : ""} onChange={(e) => setEditing({ ...editing, scheduled_end: e.target.value })} /></Field>
              </div>
              <Field label="Notes"><Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></Field>
              {editing.blueprint_id && (
                <Button variant="outline" onClick={() => openBlueprintPdf(editing.blueprint_id)} className="rounded-sm w-full" data-testid="fab-dialog-blueprint-btn">
                  <PencilRuler size={14} className="mr-1.5" /> Open fabrication blueprint PDF
                </Button>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-fab-btn" onClick={() => save.mutate(editing)} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
